"use client";

import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  useToast,
  IconButton,
  HStack,
  Divider,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  FormControl,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import { ChevronDownIcon, DeleteIcon } from "@chakra-ui/icons";
import { Copy, Phone } from "lucide-react";

import type { PhoneNumber } from "../../voice-services/phoneNumberService";
import { phoneNumberService } from "../../voice-services/phoneNumberService";
import { getDisplayPhonePretty } from "../../voice-services/voicePhoneDisplay";
import {
  primaryInboundAgentId,
  primaryOutboundAgentId,
} from "../../voice-services/phoneNumberAgents";
import { EditablePhoneNumberName } from "./EditablePhoneNumberName";
import { MakeOutboundCallDialog } from "./MakeOutboundCallDialog";

interface PhoneNumberDetailsProps {
  phoneNumber: PhoneNumber;
  agents: Array<{ id: string; name: string }>;
  onUpdate: () => void;
  onDelete: () => void;
}

interface CustomSelectProps {
  label: string;
  value: string;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  onSelect: (value: string) => void;
  isDisabled: boolean;
  cardBg: string;
  cardComponentsBg: string;
  textColor: string;
  borderColor: string;
}

// A custom select component built with Chakra UI's Menu components for full styling control.
const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  value,
  placeholder,
  options,
  onSelect,
  isDisabled,
  cardBg,
  cardComponentsBg,
  textColor,
  borderColor,
}) => {
  const selectedOption = options.find((opt) => opt.value === value);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Box>
      <Text mb={2} fontWeight="medium" color={textColor}>
        {label}
      </Text>
      <Menu>
        <MenuButton
          as={Button}
          ref={buttonRef}
          rightIcon={<ChevronDownIcon />}
          isDisabled={isDisabled}
          bg={cardBg}
          color={textColor}
          borderColor={borderColor}
          borderWidth="1px"
          _hover={{ bg: cardBg }}
          _expanded={{ bg: cardBg }}
          w="100%"
          textAlign="left"
        >
          {selectedOption ? selectedOption.label : placeholder}
        </MenuButton>
        <MenuList
          bg={cardBg}
          borderColor={borderColor}
          minW={
            buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "100%"
          }
        >
          <MenuItem
            onClick={() => onSelect("")}
            _hover={{ bg: "gray.300" }}
            bg={cardComponentsBg}
            color={textColor}
          >
            {placeholder}
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onSelect(option.value)}
              _hover={{ bg: "gray.300" }}
              bg={cardComponentsBg}
              color={textColor}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

const getProviderDisplay = (type: string): string => {
  switch (type) {
    case "retell-twilio":
      return "Twilio";
    case "retell-telnyx":
      return "Telnyx";
    case "custom":
      return "Custom telephony";
    default:
      return "Retell";
  }
};

export const PhoneNumberDetails: React.FC<PhoneNumberDetailsProps> = ({
  phoneNumber,
  agents,
  onUpdate,
  onDelete,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();

  // Add this state for the outbound call dialog
  const {
    isOpen: isCallDialogOpen,
    onOpen: onCallDialogOpen,
    onClose: onCallDialogClose,
  } = useDisclosure();

  // Define theme-based colors
  const cardBg = "transparent";
  const textColor = "gray.900";
  const cardComponentsBg = "white";
  const borderColor = "gray.500";

  const [inboundWebhookUrl, setInboundWebhookUrl] = useState<string>(
    phoneNumber.inbound_webhook_url || ""
  );

  useEffect(() => {
    setInboundWebhookUrl(phoneNumber.inbound_webhook_url || "");
  }, [phoneNumber]);

  const handleUpdate = async (
    field: string,
    value: string | null | Array<{ agent_id: string; weight: number }>,
  ) => {
    setIsUpdating(true);
    try {
      await phoneNumberService.update(phoneNumber.phone_number, {
        [field]: value,
      });
      onUpdate();
      toast({
        title: "Phone number updated",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error updating phone number",
        description:
          error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInboundWebhookUrlUpdate = useCallback(async () => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      await phoneNumberService.update(phoneNumber.phone_number, {
        inbound_webhook_url: inboundWebhookUrl || null,
      });

      toast({
        title: "Inbound Webhook URL updated",
        status: "success",
        duration: 2000,
      });
      onUpdate();
    } catch (error: any) {
      console.error("Error updating inbound webhook URL:", error);
      toast({
        title: "Error updating inbound webhook URL",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });

      // Reset to initial value on error
      setInboundWebhookUrl(phoneNumber.inbound_webhook_url || "");
    } finally {
      setIsUpdating(false);
    }
  }, [
    phoneNumber.phone_number,
    inboundWebhookUrl,
    isUpdating,
    toast,
    phoneNumber.inbound_webhook_url,
    onUpdate,
  ]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await phoneNumberService.delete(phoneNumber.phone_number);
      onDelete();
      toast({
        title: "Phone number deleted",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error deleting phone number",
        description:
          error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
      onClose();
    }
  };

  // Prepare the options for the custom selects
  const agentOptions = agents.map((agent) => ({
    value: agent.id,
    label: agent.name,
  }));

  const handleCopyId = () => {
    navigator.clipboard.writeText(phoneNumber.phone_number);
    toast({
      title: "Phone number ID copied",
      status: "success",
      duration: 2000,
    });
  };

  return (
    <Box p={6} bg={cardBg} borderRadius="md">
      <VStack align="stretch" spacing={6}>
        <Box>
          {/* Row: EditablePhoneNumberName on the left, Delete button on the right (aligned at bottom) */}
          <HStack alignItems="flex-end" justifyContent="space-between">
            <EditablePhoneNumberName
              phoneNumber={phoneNumber.phone_number}
              nickname={phoneNumber.nickname}
              prettyNumber={getDisplayPhonePretty(phoneNumber)}
              onUpdate={onUpdate}
            />

            <HStack spacing={2}>
              <IconButton
                aria-label="Make outbound call"
                icon={<Phone size={16} />}
                onClick={onCallDialogOpen}
                variant="outline"
                colorScheme="green"
                color={"green.300"}
                _hover={{ bg: "gray.200" }}
                size="sm"
                isDisabled={!primaryOutboundAgentId(phoneNumber)}
                title={
                  primaryOutboundAgentId(phoneNumber)
                    ? "Make outbound call"
                    : "Configure outbound agent first"
                }
              />
              <IconButton
                aria-label="Delete phone number"
                icon={<DeleteIcon />}
                onClick={onOpen}
                variant="outline"
                colorScheme="red"
                color={"red.500"}
                _hover={{ bg: "gray.200" }}
                size="sm"
              />
            </HStack>
          </HStack>

          {/* Below row: ID / Provider info */}
          <HStack spacing={4} mt={2} color={"gray.600"} fontSize="sm">
            <Text color="gray.500" fontSize="sm">
              ID: {getDisplayPhonePretty(phoneNumber)}
              <IconButton
                aria-label="Copy ID"
                icon={<Copy size={12} />}
                onClick={handleCopyId}
                size="xs"
                variant="ghost"
                color="gray.500"
              />
              &nbsp;&bull;&nbsp; Provider:{" "}
              {(() => {
                const domain = process.env.REACT_APP_DOMAIN_NAME || "";
                const parts = domain.split(".");
                if (parts.length === 2) {
                  return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
                } else if (parts.length === 3) {
                  return parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
                } else {
                  return "Custom Telephony";
                }
              })()}
            </Text>
          </HStack>
        </Box>

        <Divider />

        <VStack align="stretch" spacing={4}>
          <CustomSelect
            label="Inbound call agent"
            value={primaryInboundAgentId(phoneNumber) || ""}
            placeholder="None (disable inbound)"
            onSelect={(val) =>
              handleUpdate(
                "inbound_agents",
                val ? [{ agent_id: val, weight: 1 }] : [],
              )
            }
            isDisabled={isUpdating}
            cardBg={cardBg}
            cardComponentsBg={cardComponentsBg}
            textColor={textColor}
            borderColor={borderColor}
            options={agentOptions}
          />

          <CustomSelect
            label="Outbound call agent"
            value={primaryOutboundAgentId(phoneNumber) || ""}
            placeholder="None (disable outbound)"
            onSelect={(val) =>
              handleUpdate(
                "outbound_agents",
                val ? [{ agent_id: val, weight: 1 }] : [],
              )
            }
            isDisabled={isUpdating}
            cardBg={cardBg}
            cardComponentsBg={cardComponentsBg}
            textColor={textColor}
            borderColor={borderColor}
            options={agentOptions}
          />

          <FormControl>
            <FormLabel fontSize="sm" fontWeight="medium" color={"gray.700"}>
              Inbound Call Webhook URL
            </FormLabel>
            <Text fontSize="xs" color={"gray.600"} mb={2}>
              The webhook for retrieving dynamic variables for inbound phone
              calls.
              {/* {" "} */}
              {/* <Link
                href="https://docs.retellai.com/build/dynamic-variables#add-and-test-dynamic-variables"
                color={ "gray.600"}
                isExternal
              >
                (Learn more) <ExternalLinkIcon mx="2px" />
              </Link> */}
            </Text>
            <Input
              value={inboundWebhookUrl}
              onChange={(e) => setInboundWebhookUrl(e.target.value)}
              onBlur={handleInboundWebhookUrlUpdate}
              placeholder="https://your-webhook-url.com/inbound"
              size="sm"
              bg={"white"}
              borderColor={"gray.300"}
            />
          </FormControl>
        </VStack>

        <Divider />
      </VStack>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardComponentsBg}>
            <AlertDialogHeader color={textColor}>
              Delete Phone Number
            </AlertDialogHeader>
            <AlertDialogBody color={textColor}>
              Are you sure you want to delete this phone number? This action
              cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                variant="ghost"
                color="black"
                _hover={{ bg: "gray.100" }}
                ref={cancelRef}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                ml={3}
                isLoading={isDeleting}
                colorScheme="red"
                bg="red.500"
                _hover={{ bg: "red.600" }}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Add the outbound call dialog */}
      <MakeOutboundCallDialog
        isOpen={isCallDialogOpen}
        onClose={onCallDialogClose}
        phoneNumber={phoneNumber.phone_number}
        agentId={primaryOutboundAgentId(phoneNumber)}
      />
    </Box>
  );
};
